import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { ScreenService } from '../../../services/screen-service.service';

@Component({
  selector: 'app-table-paginator',
  templateUrl: './table-paginator.component.html',
  styleUrls: ['./table-paginator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, NgFor, AsyncPipe]
})
export class TablePaginatorComponent implements OnChanges {
  @Input() totalCount!: number;
  @Input() pageSize: number | null = 10;
  @Input() skip = 0;
  @Output() skipChanged = new EventEmitter<number>();

  private screenService = inject(ScreenService);

  private _skip = new BehaviorSubject<number>(0);
  public skip$ = this._skip as Observable<number>;

  private _pageSize = new BehaviorSubject<number | null>(null);
  public pageSize$ = this._pageSize as Observable<number | null>;

  private _totalCount = new BehaviorSubject<number>(0);
  public totalCount$ = this._totalCount as Observable<number>;

  private isMobile$ = this.screenService.trueIsMobile$;

  public currentPage$ = combineLatest([this.skip$, this.pageSize$]).pipe(
    map(([skip, pageSize]) => {
      const size = pageSize ?? 1;
      return size > 0 ? Math.floor(skip / size) + 1 : 1;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public pageCount$ = combineLatest([this.pageSize$, this.totalCount$]).pipe(
    map(([pageSize, totalCount]) => {
      const top = pageSize ?? 0;
      const totalRows = totalCount ?? 0;
      if (top === 0 || totalRows === 0) return 1;
      return Math.ceil(totalRows / top);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public paginationButtonsArray$ = combineLatest([this.currentPage$, this.pageCount$, this.isMobile$]).pipe(
    map(([currentPage, pageCount, isMobile]) => this.generatePagination(currentPage, pageCount, isMobile)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public showSkipButtons$ = this.pageCount$.pipe(map(pageCount => pageCount > 4));

  public disableBackButtons$ = this.currentPage$.pipe(map(page => page === 1));
  public disableForwardButtons$ = combineLatest([this.currentPage$, this.pageCount$]).pipe(
    map(([page, pageCount]) => page === pageCount)
  );

  public showLeftEllipsis$ = combineLatest([this.currentPage$, this.pageCount$, this.isMobile$]).pipe(
    map(([currentPage, pageCount, isMobile]) => {
      if (isMobile && pageCount > 4) {
        return currentPage >= 3;
      }
      return pageCount > 4 && currentPage >= 5;
    })
  );
  public showRightEllipsis$ = combineLatest([this.currentPage$, this.pageCount$, this.isMobile$]).pipe(
    map(([currentPage, pageCount, isMobile]) => {
      if (isMobile && pageCount > 4) {
        return currentPage < pageCount;
      }
      return pageCount > 4 && currentPage < pageCount;
    })
  );

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.pageSize) {
      this._pageSize.next(this.pageSize);
    }
    if (changes.totalCount) {
      this._totalCount.next(this.totalCount);
    }
    if (changes.skip) {
      this._skip.next(this.skip);
    }
  }

  public nextPageClicked(): void {
    combineLatest([this.currentPage$, this.pageCount$, this.pageSize$])
      .pipe(take(1))
      .subscribe(([currentPage, pageCount, pageSize]) => {
        if (currentPage < pageCount) {
          const newSkip = currentPage * (pageSize ?? 0);
          this.skipChanged.emit(newSkip);
        }
      });
  }

  public previousPageClicked(): void {
    combineLatest([this.currentPage$, this.pageSize$])
      .pipe(take(1))
      .subscribe(([currentPage, pageSize]) => {
        if (currentPage > 1) {
          const newSkip = (currentPage - 2) * (pageSize ?? 0);
          this.skipChanged.emit(newSkip);
        }
      });
  }
  public skipToBeginning(): void {
    this.skipChanged.emit(0);
  }

  public skipToEnd(): void {
    combineLatest([this.pageCount$, this.pageSize$])
      .pipe(take(1))
      .subscribe(([pageCount, pageSize]) => {
        const newSkip = (pageCount - 1) * (pageSize ?? 0);
        this.skipChanged.emit(newSkip);
      });
  }

  public pageClicked(page: number): void {
    combineLatest([this.pageSize$, this.currentPage$])
      .pipe(take(1))
      .subscribe(([pageSize, currentPage]) => {
        if (page === currentPage) return;
        const newSkip = (page - 1) * (pageSize ?? 0);
        this.skipChanged.emit(newSkip);
      });
  }

  private generatePagination(currentPage: number, totalPages: number, isMobile: boolean): number[] {
    const maxPageButtons = isMobile && totalPages > 4 ? 2 : 4;
    if (totalPages <= maxPageButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= maxPageButtons) {
      return Array.from({ length: maxPageButtons }, (_, i) => i + 1);
    } else {
      const startPage = Math.max(currentPage - maxPageButtons + 1, 1);
      return Array.from({ length: maxPageButtons }, (_, i) => startPage + i);
    }
  }
}
